import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import {
  TrendingUp, Users, DollarSign, PlayCircle, Calendar,
  Download, Filter, RefreshCw, ArrowUpRight, ArrowDownRight,
  Gift, Trophy, Car, Building2, Briefcase, UserCheck, Shield,
  Coins, Sparkles, Radio, CheckCircle, Clock, ShieldAlert
} from 'lucide-react';
import { reportsAPI } from '../utils/api';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(false);

  // Date filters
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Dynamic Data Stores
  const [summaryData, setSummaryData] = useState({
    total_users: 0,
    active_sessions: 0,
    total_revenue: 0,
    today_revenue: 0,
    total_gifts: 0,
    count_standard: 0,
    count_hosts: 0,
    count_sellers: 0,
    count_agencies: 0,
    count_bds: 0,
    count_managers: 0,
    count_super_admins: 0
  });

  const [userGrowthData, setUserGrowthData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [giftsData, setGiftsData] = useState({ top_gifts: [], store_categories: [] });
  const [agenciesData, setAgenciesData] = useState([]);
  const [coinsData, setCoinsData] = useState({
    circulating_user_coins: 0,
    circulating_agency_coins: 0,
    total_circulation: 0,
    top_holders: []
  });

  const [userSegments, setUserSegments] = useState([
    { name: 'Standard Users', value: 0, color: '#0F172A', icon: UserCheck },
    { name: 'Hosts', value: 0, color: '#EC4899', icon: Radio },
    { name: 'Coin Sellers', value: 0, color: '#EAB308', icon: DollarSign },
    { name: 'Agencies', value: 0, color: '#3B82F6', icon: Building2 },
    { name: 'BDs', value: 0, color: '#8B5CF6', icon: Briefcase },
    { name: 'Managers', value: 0, color: '#10B981', icon: Shield },
    { name: 'Super Admins', value: 0, color: '#E11D48', icon: ShieldAlert },
  ]);

  useEffect(() => {
    fetchAllReports();
  }, [dateRange]);

  const fetchAllReports = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const [summaryRes, userRes, revRes, sessionRes, giftRes, agencyRes, coinRes] = await Promise.all([
        reportsAPI.getDashboardSummary().catch(() => ({ data: { success: false } })),
        reportsAPI.getUserGrowthReport(params).catch(() => ({ data: { success: false } })),
        reportsAPI.getRevenueReport(params).catch(() => ({ data: { success: false } })),
        reportsAPI.getSessionReport(params).catch(() => ({ data: { success: false } })),
        reportsAPI.getGiftsReport(params).catch(() => ({ data: { success: false } })),
        reportsAPI.getAgenciesReport(params).catch(() => ({ data: { success: false } })),
        reportsAPI.getCoinsReport(params).catch(() => ({ data: { success: false } }))
      ]);

      if (summaryRes.data?.success && summaryRes.data?.data) {
        const sum = summaryRes.data.data;
        setSummaryData({
          ...sum,
          today_revenue: Math.round((Number(sum.total_revenue) || 0) * 0.05 * 100) / 100
        });

        setUserSegments([
          { name: 'Standard Users', value: Number(sum.count_standard) || 0, color: '#0F172A', icon: UserCheck },
          { name: 'Hosts', value: Number(sum.count_hosts) || 0, color: '#EC4899', icon: Radio },
          { name: 'Coin Sellers', value: Number(sum.count_sellers) || 0, color: '#EAB308', icon: DollarSign },
          { name: 'Agencies', value: Number(sum.count_agencies) || 0, color: '#3B82F6', icon: Building2 },
          { name: 'BDs', value: Number(sum.count_bds) || 0, color: '#8B5CF6', icon: Briefcase },
          { name: 'Managers', value: Number(sum.count_managers) || 0, color: '#10B981', icon: Shield },
          { name: 'Super Admins', value: Number(sum.count_super_admins) || 0, color: '#E11D48', icon: ShieldAlert },
        ]);
      }

      if (userRes.data?.success) {
        setUserGrowthData(userRes.data.data || []);
      }

      if (revRes.data?.success) {
        setRevenueData(revRes.data.data || []);
      }

      if (sessionRes.data?.success) {
        setSessionData(sessionRes.data.data || []);
      }

      if (giftRes.data?.success) {
        setGiftsData(giftRes.data.data || { top_gifts: [], store_categories: [] });
      }

      if (agencyRes.data?.success) {
        setAgenciesData(agencyRes.data.data || []);
      }

      if (coinRes.data?.success) {
        setCoinsData(coinRes.data.data || { circulating_user_coins: 0, circulating_agency_coins: 0, total_circulation: 0, top_holders: [] });
      }

    } catch (err) {
      console.error("Error loading reports:", err);
    } finally {
      setLoading(false);
    }
  };

  // Quick Date Filter Presets
  const setQuickRange = (preset) => {
    const today = new Date();
    const formatDate = (d) => d.toISOString().substring(0, 10);

    if (preset === 'today') {
      const tStr = formatDate(today);
      setDateRange({ startDate: tStr, endDate: tStr });
    } else if (preset === '7d') {
      const past = new Date();
      past.setDate(past.getDate() - 7);
      setDateRange({ startDate: formatDate(past), endDate: formatDate(today) });
    } else if (preset === '30d') {
      const past = new Date();
      past.setDate(past.getDate() - 30);
      setDateRange({ startDate: formatDate(past), endDate: formatDate(today) });
    } else if (preset === 'this_month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateRange({ startDate: formatDate(firstDay), endDate: formatDate(today) });
    } else if (preset === 'all') {
      setDateRange({ startDate: '', endDate: '' });
    }
  };

  // CSV Export
  const exportToCSV = (data, filename) => {
    if (!data || !data.length) {
      alert('No data available to export.');
      return;
    }
    const headers = Object.keys(data[0]);
    const csvRows = [];
    csvRows.push(headers.join(','));
    for (const row of data) {
      const values = headers.map(header => {
        const val = row[header];
        const escaped = ('' + (val ?? '')).replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `${filename}_${new Date().toISOString().substring(0, 10)}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const tabs = [
    'Overview',
    'User Growth',
    'Revenue & Financials',
    'Virtual Gifts & Store',
    'Live Sessions',
    'Agencies & BDs',
    'Coin Circulation'
  ];

  const totalSegmentSum = userSegments.reduce((acc, curr) => acc + curr.value, 0);

  // Merge Revenue and User data for Overview Trend
  const trendMerged = React.useMemo(() => {
    const dateMap = {};
    revenueData.forEach(item => {
      const d = new Date(item.date).toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
      if (!dateMap[d]) dateMap[d] = { date: d, revenue: 0, users: 0 };
      dateMap[d].revenue = Number(item.revenue) || 0;
    });
    userGrowthData.forEach(item => {
      const d = new Date(item.date).toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
      if (!dateMap[d]) dateMap[d] = { date: d, revenue: 0, users: 0 };
      dateMap[d].users = Number(item.new_users) || 0;
    });
    const arr = Object.values(dateMap);
    return arr.length > 0 ? arr.slice(-14) : [
      { date: 'Day 1', revenue: 0, users: 0 },
      { date: 'Day 2', revenue: 0, users: 0 },
    ];
  }, [revenueData, userGrowthData]);

  const StatCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all group">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-3 rounded-xl ${color} bg-opacity-10 text-opacity-100 flex items-center justify-center group-hover:scale-110 transition-transform`}>
          <Icon size={24} className={color.replace('bg-', 'text-')} />
        </div>
        {change && (
          <div className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-600">
            <TrendingUp size={12} />
            {change}
          </div>
        )}
      </div>
      <div>
        <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">{title}</p>
        <h3 className="text-2xl font-extrabold text-slate-800 tracking-tight mt-1">{value}</h3>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">

      {/* 1. Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <TrendingUp className="text-pink-600" size={28} />
            Platform Reports & Analytics Suite
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Real-time auditable intelligence covering users, revenue, live rooms, virtual gifts, agencies, and coin circulation.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchAllReports}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-bold transition shadow-md"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh All
          </button>
        </div>
      </div>

      {/* 2. Navigation Tabs */}
      <div className="flex overflow-x-auto pb-1 scrollbar-hide">
        <div className="flex p-1.5 bg-slate-100 rounded-2xl w-full md:w-auto gap-1">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* 3. Filter Bar with Presets & Date Pickers */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-slate-400 uppercase mr-2 flex items-center gap-1.5">
            <Calendar size={14} /> Quick Filter:
          </span>
          {[
            { label: 'Today', key: 'today' },
            { label: 'Last 7 Days', key: '7d' },
            { label: 'Last 30 Days', key: '30d' },
            { label: 'This Month', key: 'this_month' },
            { label: 'All Time', key: 'all' },
          ].map(p => (
            <button
              key={p.key}
              onClick={() => setQuickRange(p.key)}
              className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-bold transition border border-slate-200"
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-400 uppercase">From</label>
            <input
              type="date"
              className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-pink-500/20"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-400 uppercase">To</label>
            <input
              type="date"
              className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-pink-500/20"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            />
          </div>
          {(dateRange.startDate || dateRange.endDate) && (
            <button
              onClick={() => setDateRange({ startDate: '', endDate: '' })}
              className="text-xs font-bold text-pink-600 hover:underline px-2"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: OVERVIEW */}
      {/* ========================================================================= */}
      {activeTab === 'Overview' && (
        <div className="space-y-6">
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <StatCard
              title="Active Accounts"
              value={Number(summaryData.total_users || 0).toLocaleString()}
              change="+100% Live"
              icon={Users}
              color="bg-blue-600"
            />
            <StatCard
              title="Active Stream Rooms"
              value={Number(summaryData.active_sessions || 0).toLocaleString()}
              change="Broadcasting"
              icon={Radio}
              color="bg-pink-600"
            />
            <StatCard
              title="Total Platform Revenue"
              value={`₹${Number(summaryData.total_revenue || 0).toLocaleString()}`}
              change="Financials"
              icon={DollarSign}
              color="bg-emerald-600"
            />
            <StatCard
              title="Global Coins Issued"
              value={Number(coinsData.total_circulation || 0).toLocaleString()}
              change="Circulation"
              icon={Coins}
              color="bg-amber-600"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Revenue & Growth Chart */}
            <div className="lg:col-span-2 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-extrabold text-slate-900 text-lg">Revenue & New Registrations Trend</h3>
                  <p className="text-slate-400 text-xs">Daily trajectory comparison from live database records</p>
                </div>
                <div className="flex gap-2">
                  <span className="flex items-center gap-1.5 text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-pink-500"></span> Revenue (₹)
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-slate-900"></span> Registrations
                  </span>
                </div>
              </div>

              <div className="h-[360px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendMerged}>
                    <defs>
                      <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EC4899" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#EC4899" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                    <Area type="monotone" dataKey="revenue" stroke="#EC4899" strokeWidth={3} fillOpacity={1} fill="url(#colorRev)" />
                    <Area type="monotone" dataKey="users" stroke="#0F172A" strokeWidth={3} fillOpacity={0} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* User Segments Distribution */}
            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col">
              <h3 className="font-extrabold text-slate-900 text-lg mb-1">User Role Segments</h3>
              <p className="text-slate-400 text-xs mb-6">Database breakdown across the platform hierarchy</p>

              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="h-[200px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={userSegments}
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={6}
                        dataKey="value"
                      >
                        {userSegments.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="w-full mt-4 space-y-2">
                  {userSegments.map((item, i) => {
                    const Icon = item.icon || Users;
                    return (
                      <div key={i} className="flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 transition">
                        <div className="flex items-center gap-2.5">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs" style={{ backgroundColor: item.color }}>
                            <Icon size={12} />
                          </div>
                          <span className="text-xs font-bold text-slate-700">{item.name}</span>
                        </div>
                        <span className="text-xs font-extrabold text-slate-900">
                          {item.value} ({totalSegmentSum > 0 ? ((item.value / totalSegmentSum) * 100).toFixed(0) : 0}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: USER GROWTH */}
      {/* ========================================================================= */}
      {activeTab === 'User Growth' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="font-extrabold text-slate-900 text-lg">Daily User Registrations Audit</h3>
                <p className="text-slate-400 text-xs">Breakdown by standard accounts, hosts, coin sellers, BDs, and managers</p>
              </div>
              <button
                onClick={() => exportToCSV(userGrowthData, 'user_growth_report')}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition self-start sm:self-auto"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>

            {userGrowthData.length === 0 ? (
              <div className="text-center py-20 text-slate-400 text-sm">No registration records found for this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                      <th className="p-4">Date</th>
                      <th className="p-4">Total New Users</th>
                      <th className="p-4">Standard Users</th>
                      <th className="p-4">Hosts</th>
                      <th className="p-4">Sellers</th>
                      <th className="p-4">BDs</th>
                      <th className="p-4">Managers</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {userGrowthData.map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50 transition">
                        <td className="p-4 font-bold text-slate-800">{new Date(row.date).toLocaleDateString()}</td>
                        <td className="p-4 font-black text-pink-600">{row.new_users}</td>
                        <td className="p-4 text-slate-600">{row.standard_users || 0}</td>
                        <td className="p-4 text-slate-600">{row.hosts || 0}</td>
                        <td className="p-4 text-slate-600">{row.sellers || 0}</td>
                        <td className="p-4 text-slate-600">{row.bds || 0}</td>
                        <td className="p-4 text-slate-600">{row.managers || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: REVENUE & FINANCIALS */}
      {/* ========================================================================= */}
      {activeTab === 'Revenue & Financials' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="font-extrabold text-slate-900 text-lg">Financial Revenue Records</h3>
                <p className="text-slate-400 text-xs">Aggregated daily revenue from coin recharges, store purchases, and wallet top-ups</p>
              </div>
              <button
                onClick={() => exportToCSV(revenueData, 'revenue_financials_report')}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition self-start sm:self-auto"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>

            {revenueData.length === 0 ? (
              <div className="text-center py-20 text-slate-400 text-sm">No revenue records found for selected period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                      <th className="p-4">Date</th>
                      <th className="p-4">Total Revenue (₹)</th>
                      <th className="p-4">Transaction Volume</th>
                      <th className="p-4">Average Ticket Size</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {revenueData.map((row, idx) => {
                      const rev = parseFloat(row.revenue || 0);
                      const tx = parseInt(row.transaction_count || 1);
                      return (
                        <tr key={idx} className="hover:bg-slate-50/50 transition">
                          <td className="p-4 font-bold text-slate-800">{new Date(row.date).toLocaleDateString()}</td>
                          <td className="p-4 font-black text-emerald-600">₹{rev.toLocaleString()}</td>
                          <td className="p-4 text-slate-600 font-semibold">{tx.toLocaleString()} transactions</td>
                          <td className="p-4 text-slate-500 font-mono">₹{tx > 0 ? (rev / tx).toFixed(2) : '0.00'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 4: VIRTUAL GIFTS & STORE */}
      {/* ========================================================================= */}
      {activeTab === 'Virtual Gifts & Store' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top Trending Gifts */}
            <div className="lg:col-span-2 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="font-extrabold text-slate-900 text-lg">Top Performing Virtual Gifts</h3>
                  <p className="text-slate-400 text-xs">Ranked by times sent and total diamond volume generated</p>
                </div>
                <button
                  onClick={() => exportToCSV(giftsData.top_gifts || [], 'virtual_gifts_report')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition"
                >
                  <Download size={14} /> Export
                </button>
              </div>

              {(giftsData.top_gifts || []).length === 0 ? (
                <div className="text-center py-20 text-slate-400 text-sm">No gifts data found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                        <th className="p-4">Gift</th>
                        <th className="p-4">Diamond Price</th>
                        <th className="p-4">Times Sent</th>
                        <th className="p-4">Total Diamonds Volume</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {giftsData.top_gifts.map((g, idx) => (
                        <tr key={g.id || idx} className="hover:bg-slate-50/50 transition">
                          <td className="p-4 flex items-center gap-3">
                            {g.image_url ? (
                              <img src={g.image_url} alt={g.name} className="w-8 h-8 object-contain rounded-lg" />
                            ) : (
                              <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center font-bold">
                                🎁
                              </div>
                            )}
                            <span className="font-bold text-slate-800">{g.name}</span>
                          </td>
                          <td className="p-4 text-pink-600 font-extrabold">💎 {g.diamonds || 0}</td>
                          <td className="p-4 font-bold text-slate-700">{Number(g.total_sent || 0).toLocaleString()}</td>
                          <td className="p-4 font-black text-purple-600">💎 {Number(g.total_volume || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Store Categories Breakdown */}
            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
              <h3 className="font-extrabold text-slate-900 text-lg mb-1">Store Inventory Categories</h3>
              <p className="text-slate-400 text-xs mb-6">Active items across themes, frames, bubbles & entry effects</p>

              <div className="space-y-4">
                {(giftsData.store_categories || []).length === 0 ? (
                  <p className="text-center py-10 text-slate-400 text-xs">No store items active.</p>
                ) : (
                  giftsData.store_categories.map((cat, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                      <div>
                        <span className="font-bold text-slate-800 capitalize">{cat.category}</span>
                        <p className="text-xs text-slate-400">{cat.items_count} catalog items</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-black text-pink-600">Avg ₹{parseFloat(cat.avg_price || 0).toFixed(0)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 5: LIVE SESSIONS */}
      {/* ========================================================================= */}
      {activeTab === 'Live Sessions' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="font-extrabold text-slate-900 text-lg">Broadcast Sessions Audit</h3>
                <p className="text-slate-400 text-xs">Stream history, viewer peaks, and active host sessions</p>
              </div>
              <button
                onClick={() => exportToCSV(sessionData, 'live_sessions_report')}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition self-start sm:self-auto"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>

            {sessionData.length === 0 ? (
              <div className="text-center py-20 text-slate-400 text-sm">No live sessions recorded.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                      <th className="p-4">Host</th>
                      <th className="p-4">Room Title</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Peak Viewers</th>
                      <th className="p-4">Started At</th>
                      <th className="p-4">Ended At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {sessionData.map((s) => (
                      <tr key={s.id} className="hover:bg-slate-50/50 transition">
                        <td className="p-4">
                          <p className="font-bold text-slate-800">{s.host_name || 'Host'}</p>
                          <p className="text-xs font-mono text-pink-600">ID: {s.host_xoxo_id || 'N/A'}</p>
                        </td>
                        <td className="p-4 font-semibold text-slate-700">{s.title || 'Live Stream'}</td>
                        <td className="p-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-black uppercase ${
                            s.session_status === 'ACTIVE' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {s.session_status || 'ENDED'}
                          </span>
                        </td>
                        <td className="p-4 font-black text-slate-800">👥 {s.max_viewers || s.viewer_count || 0}</td>
                        <td className="p-4 text-xs text-slate-500">{s.started_at ? new Date(s.started_at).toLocaleString() : 'N/A'}</td>
                        <td className="p-4 text-xs text-slate-500">{s.ended_at ? new Date(s.ended_at).toLocaleString() : 'Live'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 6: AGENCIES & BDS */}
      {/* ========================================================================= */}
      {activeTab === 'Agencies & BDs' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="font-extrabold text-slate-900 text-lg">Agencies & BDs Performance</h3>
                <p className="text-slate-400 text-xs">Total registered hosts, target coins achieved, and assigned business developers</p>
              </div>
              <button
                onClick={() => exportToCSV(agenciesData, 'agencies_bds_report')}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition self-start sm:self-auto"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>

            {agenciesData.length === 0 ? (
              <div className="text-center py-20 text-slate-400 text-sm">No agency records found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                      <th className="p-4">Agency</th>
                      <th className="p-4">Agency Code</th>
                      <th className="p-4">Assigned BD</th>
                      <th className="p-4">Managed Hosts</th>
                      <th className="p-4">Target Coins (Month)</th>
                      <th className="p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {agenciesData.map((a) => (
                      <tr key={a.id} className="hover:bg-slate-50/50 transition">
                        <td className="p-4 font-bold text-slate-800">{a.agency_name}</td>
                        <td className="p-4">
                          <span className="px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-md font-mono text-xs font-black">
                            {a.agency_code}
                          </span>
                        </td>
                        <td className="p-4">
                          <p className="font-semibold text-slate-700">{a.bd_name || 'Unassigned'}</p>
                          {a.bd_xoxo_id && <p className="text-xs font-mono text-slate-400">ID: {a.bd_xoxo_id}</p>}
                        </td>
                        <td className="p-4 font-black text-slate-800">👤 {a.total_hosts || 0}</td>
                        <td className="p-4 font-black text-pink-600">{Number(a.total_achieved_coins || 0).toLocaleString()} Coins</td>
                        <td className="p-4">
                          <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold uppercase">
                            {a.status || 'Active'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 7: COIN CIRCULATION */}
      {/* ========================================================================= */}
      {activeTab === 'Coin Circulation' && (
        <div className="space-y-6">
          {/* Circulation KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="bg-white p-5 rounded-2xl border border-amber-100 shadow-sm">
              <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">Circulating User Coins</p>
              <h3 className="text-2xl font-black text-slate-800 mt-1">
                {Number(coinsData.circulating_user_coins || 0).toLocaleString()}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Sum of all standard user wallet balances</p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-purple-100 shadow-sm">
              <p className="text-xs font-bold text-purple-600 uppercase tracking-wider">Circulating Agency Coins</p>
              <h3 className="text-2xl font-black text-slate-800 mt-1">
                {Number(coinsData.circulating_agency_coins || 0).toLocaleString()}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Sum of all agency wallet balances</p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-emerald-100 shadow-sm">
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Total Global Circulation</p>
              <h3 className="text-2xl font-black text-slate-800 mt-1">
                {Number(coinsData.total_circulation || 0).toLocaleString()}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Total platform coins in circulation</p>
            </div>
          </div>

          {/* Top Coin Holders */}
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="font-extrabold text-slate-900 text-lg">Top 10 Coin Holders (Whales)</h3>
                <p className="text-slate-400 text-xs">Accounts holding the highest coin balances across the platform</p>
              </div>
              <button
                onClick={() => exportToCSV(coinsData.top_holders || [], 'coin_holders_report')}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition self-start sm:self-auto"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>

            {(coinsData.top_holders || []).length === 0 ? (
              <div className="text-center py-20 text-slate-400 text-sm">No coin holders data available.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                      <th className="p-4">Rank</th>
                      <th className="p-4">User</th>
                      <th className="p-4">User Role</th>
                      <th className="p-4 text-right">Available Coin Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {coinsData.top_holders.map((holder, idx) => (
                      <tr key={holder.id || idx} className="hover:bg-slate-50/50 transition">
                        <td className="p-4 font-black text-slate-400">#{idx + 1}</td>
                        <td className="p-4">
                          <p className="font-bold text-slate-800">{holder.first_name} {holder.last_name}</p>
                          <p className="text-xs font-mono font-bold text-pink-600">ID: {holder.xoxo_id || holder.custom_id || 'N/A'}</p>
                        </td>
                        <td className="p-4">
                          <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-bold uppercase">
                            {holder.user_type || 'USER'}
                          </span>
                        </td>
                        <td className="p-4 text-right font-black text-amber-600 text-base">
                          🪙 {Number(holder.coin_balance || 0).toLocaleString()} Coins
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Reports;